<template>
  <v-row style="margin-top:.2em">
    <v-col cols="12" md="10" offset-md="1" lg="8" offset-lg="2" xl="6" offset-xl="3">
      <v-card>
        <v-toolbar>
          <v-toolbar-title>Speaking Sample Upload</v-toolbar-title>
        </v-toolbar>
        <template v-if="done">
          <v-card-title>Completed!</v-card-title>
          <v-card-text>
            <p>Your speaking sample has been uploaded. Thank you!</p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn to="/" text>Return to Scots Connect Home</v-btn>
          </v-card-actions>
        </template>
        <template v-else-if="isUploading">
          <v-card-title>Uploading File</v-card-title>
          <v-card-text>
            <p>Your speaking sample is being uploaded. This should only take a moment...</p>
          </v-card-text>
        </template>
        <template v-else>
          <v-card-text>
            <v-alert class="my-4" type="info" outlined colored-border border="left">
              <p>Imagine you are recording an audio file in order to introduce yourself to a new roommate at Covenant in {{ language }}.  Provide a brief audio sample in {{ language }} in which you cover as many of the following prompts as possible:</p>
              <ul class="my-2">
                <li>Basic personal information (name, age, hometown, etc.)</li>
                <li>Your likes and dislikes</li>
                <li>One memorable experience from high school</li>
                <li>Your plans for study at Covenant</li>
                <li>What you hope to experience while a student at Covenant</li>
                <li>Where you see yourself in 5 years</li>
              </ul>
              <p>You do not have to discuss all aspects of the prompt if you are unsure of how to express your ideas, but please address as many parts of the prompt as possible in order to ensure an appropriate placement of your speaking abilities in {{ language }}.  You only have to provide an audio file for the language in which you have completed a placement test.  Please do not use any outside resources (an online translator, ChatGPT, a friend or family member, etc.) when recording the introduction.  Make the speech as natural and spontaneous as possible.  Remember, no preparation is needed for the speech sample; we are only using the information to appropriately place you in a language course.  If you have any questions, please email Dr. Brianne Stambaugh, Chair of the World Languages department (brianne.stambaugh@covenant.edu).</p>
            </v-alert>
            <p>You can either record through this page using your device's microphone, or you can use a separate audio recorder and upload the file.</p>
            <v-tabs v-model="tab">
              <v-tab>Recorder</v-tab>
              <v-tab>Upload File</v-tab>
            </v-tabs>
            <v-tabs-items v-model="tab">
              <v-tab-item>
                <p class="mt-2">Press the record button below to start recording. Your browser may ask your permission to access the microphone. Make sure to speak clearly and loud enough for your microphone to hear you. You will be able to listen to the audio clip after recording it before uploading it, and you can choose to record it again if you desire.</p>
                <v-btn :color="isRecording ? 'error' : ''" fab @click="isRecording ? stopRecording() : startRecording()" class="mt-2 ma-4">
                  <v-icon>{{ isRecording ? 'fal' : 'fas' }} fa-microphone</v-icon>
                </v-btn>
                <v-btn v-if="audioClip" fab @click="playRecording" :color="isPlaying ? 'success' : ''" class="mt-2 ma-4">
                  <v-icon>fas fa-{{ isPlaying ? 'stop' : 'play' }}</v-icon>
                </v-btn>
              </v-tab-item>
              <v-tab-item>
                <p class="mt-2">Please upload an audio recording of your response to the prompt. The uploader only accepts audio files in mp3 and wav format.</p>
                <v-file-input v-model="file" label="Upload Language Sample" accept="audio/mp3,audio/wav" outlined></v-file-input>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
          <v-card-actions>
            <v-btn v-if="tab === 1" :disabled="!file" color="success" @click="doUpload">Upload File</v-btn>
            <v-btn v-else :disabled="!audioClip" color="success" @click="saveAudio">Save Audio</v-btn>
            <v-spacer />
            <v-btn to="/" text>Return to Scots Connect Home</v-btn>
          </v-card-actions>
        </template>
      </v-card>
    </v-col>
  </v-row>
</template>
<script>
import { hasRole } from '../../helpers/has'
import { uploadFile } from '../../helpers/functions'
import { ref, onMounted, computed } from '@vue/composition-api'

const folder = '1Kav8y9uv74cX-HAr5rvBIvNha9ZnXrkq'

export default {
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user)
    const done = ref(false)
    const tab = ref(0)
    const language = ref('Spanish')
    const headers = ref({})
    const file = ref(null)
    const isUploading = ref(false)

    async function doUpload () {
      isUploading.value = true
      try {
        // Upload the file to the service/drive-upload service, and place it in folder: 1zeaBmdZn5Ccp0SLDZSAvgMO-7IgoBq1R
        const filename = user.value.bannerId + ' - ' + user.value.name + ' - ' + root.$store.state.term + ' - ' + language.value + '.wav'
        const fileId = await uploadFile({ file: file.value, filename, folder })
        if (fileId === false) {
          root.$store.dispatch('snackbar', { active: true, type: 'error', timeout: 8000, text: 'An unknown error occurred while uploading your file. Please try again. If the problem persists, please contact Technology Services at techsupport@covenant.edu.' })
          return
        }
        // Send an email to brianne.stambaugh@covenant.edu that the file was uploaded
        await root.$feathers.service('system/email').create({
          from: 'report-email@covenant.edu',
          // to: 'brianne.stambaugh@covenant.edu',
          to: 'jon.moon@covenant.edu',
          subject: 'Scots Connect Language Sample Uploaded',
          html: user.value.preferred + ' ' + user.value.last + '(' + user.value.bannerId + ') just uploaded their language speach sample through Scots Connect. Here\'s the link to the file in Google Drive: <a href="https://drive.google.com/file/d/' + fileId + '">' + filename + '</a>. You can manage all of the language test components in the Scots Portal here: <a href="https://portal.covenant.edu/student/language">https://portal.covenant.edu/student/language</a>.'
        })
        // Update the to-do list item to show that it is done
        for (let i = 0; i < root.$store.state.actions.length; i++) {
          if (root.$store.state.actions[i].link === '/action/writing') {
            const block = {
              ['actions.' + i + '.done']: true,
              ['actions.' + i + '.doneDate']: new Date(),
              ['actions.' + i + '.fileId']: fileId
            }
            let { actions } = await root.$feathers.service('orientation/tracker').patch(root.$store.state.tracker.id, block)
            root.$store.commit('setActions', actions)
            break
          }
        }
        // Set the status to done to show them the confirmation message
        done.value = true
      } catch (e) {
        root.$store.dispatch('snackbar', { active: true, type: 'error', timeout: 8000, text: 'Error uploading file: ' + e })
      }
    }

    onMounted(async () => {
      if (hasRole(user.value, 'Parent')) {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 8000, text: 'Uploading a language sample is not available for accounts registered for Family Edition. If you are an incoming Covenant student, please log in with your Covenant account to upload an audio file.' })
        root.$router.replace('/')
        return
      }
      if (root.$store.state.term === '') await root.$store.dispatch('loadTracker')
      // Check to see the status of the to-do list item
      const rec = root.$store.state.actions.find(({ link }) => link === '/action/language')
      if (rec) {
        const temp = rec.title.split(' ')
        language.value = temp[temp.length - 1]
      } else {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 8000, text: 'You do not have a language upload entry in your to-do list. You need to select a Language Test option in the Placement Tests section, then complete the written language test, for this to be added to your to-do list.' })
        // root.$router.replace('/')
      }
    })

    const isRecording = ref(false)
    const mediaRecorder = ref(null)
    const audioChunks = ref([])
    const audioBlob = ref(null)
    const audioClip = ref(null)

    async function startRecording () {
      if (isPlaying.value) await audioPlayer.value.pause()
      if (audioPlayer.value != null) audioPlayer.value = null
      if (audioClip.value != null) audioClip.value = null
      if (audioBlob.value != null) audioBlob.value = null
      isRecording.value = true
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        mediaRecorder.value = new MediaRecorder(stream)

        mediaRecorder.value.ondataavailable = event => {
          audioChunks.value.push(event.data)
        }

        mediaRecorder.value.onstop = () => {
          audioBlob.value = new Blob(audioChunks.value, { type: 'audio/wav' })
          const audioUrl = URL.createObjectURL(audioBlob.value)
          // Use audioUrl to play or download the recording
          audioClip.value = audioUrl
          audioChunks.value = []
        }

        mediaRecorder.value.start()
      } catch (e) {
        alert('There was an error starting the record process: ' + e)
      }
    }

    function stopRecording () {
      mediaRecorder.value.stop()
      isRecording.value = false
    }

    const isPlaying = ref(false)
    const audioPlayer = ref(null)
    function playRecording () {
      if (isPlaying.value) {
        audioPlayer.value.pause()
        isPlaying.value = false
      } else {
        if (audioPlayer.value == null) {
          audioPlayer.value = new Audio(audioClip.value)
          audioPlayer.value.onended = (e) => {
            isPlaying.value = false
          }
        }
        audioPlayer.value.play()
        isPlaying.value = true
      }
    }

    async function saveAudio () {
      const service = root.$feathers.service('orientation/language-placement')
      const filename = user.value.bannerId + ' - ' + user.value.name + ' - ' + root.$store.state.term + ' - ' + language.value + '.wav'
      const uploadLink = await uploadFile({ file: audioBlob.value, filename, contentType: 'audio/wav', folder })
      const { data } = await service.find({ query: { pidm: user.value.pidm, language: { $regex: language.value } } })
      if (data.length > 0) {
        await service.patch(data[0]._id, { uploadLink })
      }
      const index = root.$store.state.actions.findIndex(({ link }) => link === '/action/language')
      if (index >= 0) {
        const block = {
          ['actions.' + index + '.done']: true,
          ['actions.' + index + '.doneDate']: new Date(),
          ['actions.' + index + '.fileId']: uploadLink
        }
        let { actions } = await root.$feathers.service('orientation/tracker').patch(root.$store.state.tracker.id, block)
        root.$store.commit('setActions', actions)
        done.value = true
      }
    }

    return {
      user,
      done,
      tab,
      language,
      headers,
      file,
      isUploading,
      doUpload,
      isRecording,
      audioClip,
      startRecording,
      stopRecording,
      isPlaying,
      playRecording,
      saveAudio
    }
  }
}
</script>
